import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, DatePicker, toast } from '../../components/common';
import { budgetApi } from '../../services/api';

export default function FiscalYearForm({ onSuccess, onCancel }) {
    const [form, setForm] = useState({
        year: new Date().getFullYear() + 1,
        startDate: '',
        endDate: ''
    });
    const [errors, setErrors] = useState({});

    const mutation = useMutation({
        mutationFn: (data) => budgetApi.createFiscalYear(data),
        onSuccess: () => {
            toast.success('Fiscal year created successfully');
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to create fiscal year');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.year) newErrors.year = 'Year is required';
        if (!form.startDate) newErrors.startDate = 'Start date is required';
        if (!form.endDate) newErrors.endDate = 'End date is required';
        if (form.startDate && form.endDate && new Date(form.startDate) >= new Date(form.endDate)) {
            newErrors.endDate = 'End date must be after start date';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate({
            year: parseInt(form.year),
            startDate: form.startDate,
            endDate: form.endDate
        });
    };

    return (
        <form onSubmit={handleSubmit} className="fiscal-year-form">
            <div className="form-stack">
                <Input
                    name="year"
                    type="number"
                    label="Fiscal Year"
                    placeholder="e.g., 2025"
                    value={form.year}
                    onChange={handleChange}
                    error={errors.year}
                    required
                />
                <DatePicker
                    label="Start Date"
                    value={form.startDate}
                    onChange={(date) => setForm(prev => ({ ...prev, startDate: date }))}
                    error={errors.startDate}
                    required
                />
                <DatePicker
                    label="End Date"
                    value={form.endDate}
                    onChange={(date) => setForm(prev => ({ ...prev, endDate: date }))}
                    minDate={form.startDate}
                    error={errors.endDate}
                    required
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    Create Fiscal Year
                </Button>
            </div>
        </form>
    );
}

FiscalYearForm.propTypes = {
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
